import type { CrudActionWrite } from '#layers/access/constants/permissions/crud';
import type { CrudAction } from '#layers/access/types/permissions/crud';
import type { MonospacePolicyEntitlementResource } from '#layers/access/types/permissions/policy';
import type { EntitlementKeyParts } from '#layers/access/utils/entitlement-codec';
import type { ValueOf } from '#layers/base/public';
import type { CompositePrimaryKey, GlobalCollectionId, GlobalFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { usePermissionsStore } from '#layers/access/stores/permissions';
import { getKeyId } from '#layers/access/utils/get-key-id';

/** How a decision was reached. */
export const AuthorityKind = {
	Admin: 'admin',
	Pending: 'pending',
	Entitlement: 'entitlement',
	ItemRule: 'itemRule',
	FieldRule: 'fieldRule',
} as const;

export type AuthorityKind = ValueOf<typeof AuthorityKind>;

export interface AuthorityResult {
	allowed: boolean;
	authority: AuthorityKind;
}

/** The permissions-store surface `authorize` reads (the full store satisfies it). */
export interface AuthorizationPermissions {
	readonly isAdmin: boolean;
	readonly isReady: boolean;
	hasEntitlement: (request: EntitlementKeyParts) => boolean;
	canActionCollection: (collectionId: GlobalCollectionId, action: CrudAction) => boolean;
	canCreateFields: (collectionId: GlobalCollectionId, fields: GlobalFieldId[]) => boolean;
	canUpdateFields: (collectionId: GlobalCollectionId, fields: GlobalFieldId[]) => boolean;
}

/** The descriptor surface `authorize` reads (the accessor satisfies it). */
export interface AuthorizationDescriptor {
	resourceForCollection: (collectionId: GlobalCollectionId) => MonospacePolicyEntitlementResource | undefined;
	descriptorHasAction: (resource: MonospacePolicyEntitlementResource, action: string) => boolean;
	isScoped: (resource: MonospacePolicyEntitlementResource, action: string) => boolean;
}

export type AuthorizeRequest =
	| { collection: ResolvedMonospaceCollection; action: CrudAction; id?: CompositePrimaryKey | string }
	| { collection: ResolvedMonospaceCollection; action: CrudActionWrite; fields: GlobalFieldId[] };

/**
 * Resolve a request to `{ allowed, authority }`. The UI mirrors backend enforcement:
 * admin bypasses everything; until the descriptor has successfully loaded a non-admin is denied
 * (a failed/never-loaded descriptor is not the same as "nothing is
 * gated", so we must not fall through to the potentially-more-permissive item-rule path);
 * a collection the descriptor covers gates by entitlement; everything else gates by item rules
 * and field rules.
 */
export function authorize(
	permissions: AuthorizationPermissions,
	descriptor: AuthorizationDescriptor,
	request: AuthorizeRequest,
): AuthorityResult {
	if (permissions.isAdmin) return { allowed: true, authority: AuthorityKind.Admin };

	if (!permissions.isReady) {
		return { allowed: false, authority: AuthorityKind.Pending };
	}

	// A field request gates by field rules, assuming the action is NOT descriptor-covered. Covered
	// actions have no per-field entitlement, so callers (see use-form-block) resolve coverage first
	// and gate a covered action through the collection-action request instead of a field request.
	if ('fields' in request) {
		const allowed = request.action === 'create'
			? permissions.canCreateFields(request.collection.id, request.fields)
			: permissions.canUpdateFields(request.collection.id, request.fields);

		return { allowed, authority: AuthorityKind.FieldRule };
	}

	const { collection, action } = request;
	const resource = descriptor.resourceForCollection(collection.id);

	if (resource && descriptor.descriptorHasAction(resource, action)) {
		const entitlement: EntitlementKeyParts = descriptor.isScoped(resource, action)
			? { resource: resource.name, action, resourceId: getKeyId(request.id) }
			: { resource: resource.name, action };

		return { allowed: permissions.hasEntitlement(entitlement), authority: AuthorityKind.Entitlement };
	}

	return { allowed: permissions.canActionCollection(collection.id, action), authority: AuthorityKind.ItemRule };
}

/** Reactive entry point: binds `authorize` to the live permissions store (both gating seams). */
export function useAuthorization() {
	const permissions = usePermissionsStore();

	function check(request: AuthorizeRequest): AuthorityResult {
		return authorize(permissions, permissions, request);
	}

	function can(request: AuthorizeRequest): boolean {
		return check(request).allowed;
	}

	return {
		authorize: check,
		can,
	};
}
